import { Capacitor } from '@capacitor/core';

/**
 * AppsFlyer attribution wrapper for the Capacitor game client.
 *
 * Ported 1:1 from the Unity client (Assets/Scripts/Managers/AppsFlyerManager.cs)
 * so both clients report into the same AppsFlyer app + event schema:
 *   - init: initSDK(devKey, appId) → (iOS) wait for ATT → SDK starts
 *   - events: af_complete_registration, first_hand_played (once), first_deposit (once)
 *   - events fired before the SDK is ready are queued and flushed on init
 *
 * Native-only: on web / dev / Jest every entry point is a no-op, and the native
 * plugins are dynamically imported so the web bundle never tries to resolve them.
 */

// Event names — kept IDENTICAL to the Unity client so both feed the same schema.
export const AF_EVENT = {
  COMPLETE_REGISTRATION: 'af_complete_registration',
  FIRST_HAND: 'first_hand_played',
  FIRST_DEPOSIT: 'first_deposit'
} as const;

// Dev key + iOS App Store ID come from build-time env (CI injects the real
// values), mirroring Unity's remote-config sourcing — nothing secret is
// committed. Placeholders left unset ⇒ init is skipped.
const DEV_KEY = process.env.REACT_APP_APPSFLYER_DEV_KEY || '';
const APP_ID = process.env.REACT_APP_APPSFLYER_APP_ID || ''; // iOS App Store ID (digits only)

const ONCE_PREFIX = 'af_once_';

let ready = false;
let initStarted = false;
const pending: Array<{ eventName: string; values: Record<string, unknown> }> = [];
// In-memory record of once-per-install events already handled this session, so
// repeated calls on hot paths (e.g. the first-hand effect that re-runs on every
// game update) short-circuit without touching localStorage.
const firedOnce = new Set<string>();

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// Trigger the iOS App Tracking Transparency system prompt. The AppsFlyer SDK is
// already holding first-launch attribution (waitForATTUserAuthorization below)
// until this resolves or the timeout elapses — mirrors Unity's
// waitForATTUserAuthorizationWithTimeoutInterval(60).
async function requestIOSTrackingConsent(): Promise<void> {
  if (Capacitor.getPlatform() !== 'ios') return;
  try {
    const { AppTrackingTransparency } = await import('capacitor-plugin-app-tracking-transparency');
    const { status } = await AppTrackingTransparency.getStatus();
    if (status === 'notDetermined') {
      await AppTrackingTransparency.requestPermission();
    }
  } catch (e) {
    console.warn('[AppsFlyer] ATT consent request failed', e);
  }
}

export async function initAppsFlyer(): Promise<void> {
  if (initStarted) return; // idempotent — App mounts this once
  initStarted = true;

  if (!isNative()) return; // web / dev / Jest: no-op
  if (!DEV_KEY || !APP_ID) {
    console.warn('[AppsFlyer] dev key / app id not configured — skipping init');
    return;
  }

  try {
    const { AppsFlyer } = await import('appsflyer-capacitor-plugin');
    const isIOS = Capacitor.getPlatform() === 'ios';

    await AppsFlyer.initSDK({
      appID: APP_ID,
      devKey: DEV_KEY,
      isDebug: false,
      // iOS: hold first-launch attribution until the ATT prompt is answered (or
      // 60s elapse). No-op on Android.
      waitForATTUserAuthorization: isIOS ? 60 : 0,
      minTimeBetweenSessions: 6,
      registerConversionListener: true,
      registerOnAppOpenAttribution: false,
      useReceiptValidationSandbox: false,
      useUninstallSandbox: false
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);

    await requestIOSTrackingConsent();

    ready = true;
    flushPending();
  } catch (e) {
    console.warn('[AppsFlyer] init failed', e);
  }
}

function flushPending(): void {
  if (!ready) return;
  while (pending.length) {
    const ev = pending.shift();
    if (ev) void sendNow(ev.eventName, ev.values);
  }
}

async function sendNow(eventName: string, values: Record<string, unknown>): Promise<void> {
  try {
    const { AppsFlyer } = await import('appsflyer-capacitor-plugin');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await AppsFlyer.logEvent({ eventName, eventValue: values as any });
  } catch (e) {
    console.warn(`[AppsFlyer] logEvent(${eventName}) failed`, e);
  }
}

/**
 * Log an AppsFlyer in-app event. No-op on web/dev. Events fired before the SDK
 * finishes initializing are queued and flushed on init.
 *
 * @param opts.once Fire at most once per install (persisted in localStorage),
 *                  matching Unity's PlayerPrefs guards on first_hand_played /
 *                  first_deposit.
 */
export function logAppsFlyerEvent(
  eventName: string,
  opts: { once?: boolean; values?: Record<string, unknown> } = {}
): void {
  if (!isNative()) return;

  if (opts.once) {
    const key = ONCE_PREFIX + eventName;
    if (firedOnce.has(key)) return; // fast in-memory path — no localStorage on hot paths
    try {
      if (localStorage.getItem(key) === '1') {
        firedOnce.add(key);
        return;
      }
      localStorage.setItem(key, '1');
    } catch {
      /* private mode — best effort; fall through and still send */
    }
    firedOnce.add(key);
  }

  const values = opts.values ?? {};
  if (ready) {
    void sendNow(eventName, values);
  } else {
    pending.push({ eventName, values });
  }
}
